import { Expose } from 'class-transformer';

import type RestaurantCalendar from '../types/RestaurantCalendar';
import type CalendarRange from '../types/CalendarRange';
import type LatLong from '../types/LatLong';
import type LocationAlert from '../types/LocationAlert';
import { DateTime } from 'luxon';

export default class Location {
  @Expose() name: string = "";
  @Expose() extref: string = "";
  @Expose() id: number = 0;
  @Expose() city: string = "";
  @Expose() geo: string = "";
  @Expose() latitude: string = "";
  @Expose() longitude: string = "";
  @Expose() state: string = "";
  @Expose() streetaddress: string = "";
  @Expose() zip: string = "";
  @Expose() phone: string = "";
  @Expose() is_vip: boolean = false;
  @Expose() supportsdispatch: boolean = false;
  @Expose() dispatch_enabled: boolean = false;
  @Expose() catering_calendar: RestaurantCalendar[] = [];
  @Expose() distanceFrom?: number;
  @Expose() slug: string = '';
  @Expose() advanceorderdays: number = 0;
  @Expose() cateringadvorderdays: number = 0;
  @Expose() alerts: LocationAlert[] = [];
  @Expose() alert_enable: boolean = false;
  @Expose() global_alert: (string | undefined) = undefined;
  @Expose() catering_disabled: boolean = false;
  @Expose() catering_disabled_message: (string | undefined) = undefined;

  constructor(name: string, extref: string, id: number, city: string, geo: string, latitude: string, longitude: string, state: string, streetaddress: string, zip: string, phone: string, is_vip: boolean, catering_calendar: RestaurantCalendar[], distanceFrom: number, slug: string, advanceorderdays: number, alerts: LocationAlert[], alert_enable: boolean, global_alert: string, cateringadvorderdays: number, catering_disabled: boolean, catering_disabled_message: string) {
    this.name = name;
    this.extref = extref;
    this.id = id;
    this.city = city;
    this.geo = geo;
    this.latitude = latitude;
    this.longitude = longitude;
    this.state = state;
    this.streetaddress = streetaddress;
    this.zip = zip;
    this.phone = phone;
    this.is_vip = is_vip;
    this.catering_calendar = catering_calendar;
    this.distanceFrom = distanceFrom;
    this.slug = slug;
    this.advanceorderdays = advanceorderdays;
    this.cateringadvorderdays = cateringadvorderdays;
    this.alerts = alerts;
    this.alert_enable = alert_enable;
    this.global_alert = global_alert;
    this.catering_disabled = catering_disabled;
    this.catering_disabled_message = catering_disabled_message;
  }

  dispatchEnabled = () => {
    return (this.supportsdispatch == true && this.dispatch_enabled == true);
  }

  hoursForDate = ((dt: DateTime, handoffMode: string):CalendarRange => {
    const dayStr = dt.toFormat('yyyyMMdd');

    let cal = this.catering_calendar.find((cal: RestaurantCalendar) => {
      return cal.type == 'business'
    })

    // Grab the calendar for the actual handoff mode instead
    if (handoffMode != "pickup") {
      // Currently only supporting dispatch and pickup
      let newCal = this.catering_calendar.find((cal: RestaurantCalendar) => {
        return cal.type == 'dispatch';
      })
      if (newCal) {
        cal = newCal;
      }
      // Should grab by Delivery but the string needs manipulated
    }

    const ranges = cal?.ranges ?? [];
    const calDay = ranges.find((range: CalendarRange) => {
      return (range.start ?? '').split(" ")[0] == dayStr;
    });

    if (calDay) {
      return calDay;
    }

    return {start: '10:45am', end: '10:00pm', weekday: dayStr} as CalendarRange;
  });

  hoursForDay = ((handoffMode: string):CalendarRange => {
    const dt = DateTime.fromJSDate(new Date);
    const dayStr = dt.toFormat('EEE');

    let cal = this.catering_calendar.find((cal: RestaurantCalendar) => {
      return cal.type == 'business'
    })

    // Grab the calendar for the actual handoff mode instead
    if (handoffMode != "pickup") {
      // Currently only supporting dispatch and pickup
      let newCal = this.catering_calendar.find((cal: RestaurantCalendar) => {
        return cal.type == 'dispatch';
      })
      if (newCal) {
        cal = newCal;
      }
      // Should grab by Delivery but the string needs manipulated
    }

    const ranges = cal?.ranges ?? [];
    const calDay = ranges.find((range: CalendarRange) => {
      return range.weekday == dayStr;
    });

    if (calDay) {
      return calDay;
    }

    return {start: '10:45am', end: '10:00pm', weekday: dayStr} as CalendarRange;
  });

  getOpeningTimeForDate(day: DateTime, handoffMode: string): DateTime | null {
    let range = this.hoursForDate(day, handoffMode);

    if (range) {
      return DateTime.fromFormat(range.start, "yyyyMMdd HH:mm")
    } else {
      return null
    }
  }

  getClosingTimeForDate(day: DateTime, handoffMode: string): DateTime | null {
    let range = this.hoursForDate(day, handoffMode);

    if (range) {
      return DateTime.fromFormat(range.end, "yyyyMMdd HH:mm")
    } else {
      return null
    }
  }
}

